/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-refresh/only-export-components */
import ScoreBadge from "@/components/score-badge";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLabels } from "@/hooks/use-labels";
import {
  useAddDomainToDns,
  useRemoveDomainFromDns,
} from "@/hooks/use-postmaster";
import { usePermission } from "@/hooks/use-premission";
import { cn, dateFormatter } from "@/lib/utils";
import { AuthProvider, LabelType } from "@/types/common";
import { Tag as TagIcon } from "@phosphor-icons/react";
import type { ColDef, SortDirection } from "ag-grid-community";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import { useState } from "react";
import { authProviderIcons } from "../mailbox/esp-providers";
const tooltips = {
  uptimeAvailability:
    "Percentage of time the DNS servers are operational and responding to queries",
  responseTime: "Average time for DNS queries to be resolved",
  dnssecImplementation: "Whether DNSSEC is properly configured and validated",
  nameserverRedundancy:
    "Number of nameservers and their geographic distribution",
  recordConsistency:
    "Checks if DNS records are consistent across all authoritative nameservers",
  overallScore: "Average of all individual scores",
};

const getScoreColor = (score: number) => {
  if (score >= 90) return "text-green-500";
  if (score >= 70) return "text-yellow-500";
  if (score >= 50) return "text-orange-500";
  return "text-red-500";
};

const ScoreCellRenderer = (props: any) => {
  const { value, data, colDef } = props;
  const score = colDef.field === "overallScore" ? value : value.score;
  const displayValue =
    colDef.field === "overallScore" ? value : `${value.value}`;

  return (
    <span
      className={cn(
        "px-2 py-1 rounded-full text-xs font-medium flex items-center gap-2 h-full ",

        colDef.field === "responseTime" && getScoreColor(score)
      )}
    >
      <span className="">{displayValue}</span>

      {/* <Badge variant="outline" className={cn("text-xs border-none", getScoreColor(score))}>
        {score}
      </Badge> */}
    </span>
  );
};

const StatusCellRenderer = (props: any) => {
  const { value } = props;
  return (
    <div className="flex items-center gap-2 h-full">
      {value === "valid" ? (
        <Badge className="text-green-500 bg-green-100 hover:bg-green-100 border-none shadow-none">
          Passed
        </Badge>
      ) : (
        <Badge className="text-red-500 bg-red-100 hover:bg-red-100 border-none shadow-none">
          Failed
        </Badge>
      )}
    </div>
  );
};
const recordTypeColumns: ColDef[] = [
  "A",
  "AAAA",
  "ANAME",
  "CERT",
  "CNAME",
  "DKIM",
  "DMARC",
  "DNSKEY",
  "DS",
  "MX",
  "NS",
  "PTR",
  "SOA",
  "SPF",
  "TXT",
].map((type) => ({
  field: type,
  headerName: type,
  width: 100,
  cellRenderer: StatusCellRenderer,
  headerTooltip: `Status of ${type} record`,
}));

const LabelCellRenderer = ({
  params,
  pageMetadata,
}: {
  params: any;
  pageMetadata: {
    page: number;
    limit: number;
    search: string;
    selectedLabels: string[];
  };
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [updatingLabelId, setUpdatingLabelId] = useState<string | null>(null);
  const { mutate: addLabelsToDns } = useAddDomainToDns();
  const { mutate: removeLabelsFromDns } = useRemoveDomainFromDns();
  const { data: labels } = useLabels(1, 100, LabelType.DNS, isOpen);

  const { hasPermission } = usePermission();

  const renderLabels = () => {
    // Check if labels exist and handle accordingly
    if (!params.data.labels || params.data.labels.length === 0) {
      return <span className="text-xs text-muted-foreground">No labels</span>;
    }

    // Get the first label if it exists
    const firstLabel = params.data?.labels?.[0];

    return (
      <>
        {firstLabel && (
          <Badge
            key={firstLabel._id}
            style={{ backgroundColor: firstLabel.color }}
            className="text-background text-xs py-0.5 px-1.5 shadow-none rounded-sm"
          >
            {firstLabel.name}
          </Badge>
        )}
        {params.data.labels.length > 1 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge className="text-accent-foreground bg-accent text-xs py-0.5 px-1.5 shadow-none rounded-sm">
                +{params.data.labels.length - 1}
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="bg-black text-white">
              <div className="flex flex-col gap-1">
                {params.data?.labels?.slice(1).map((label: any) => (
                  <div key={label._id} className="flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: label?.color }}
                    />
                    <span className="text-xs">{label?.name}</span>
                  </div>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </>
    );
  };

  if (!hasPermission("email_health:edit"))
    return (
      <div className="h-full flex items-center">
        {!params.data.labels?.length ? (
          <span className="text-xs text-muted-foreground">No Labels</span>
        ) : (
          renderLabels()
        )}
      </div>
    );

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <div className="inline-flex min-w-40 justify-between h-8 px-2 rounded-md border border-input hover:bg-accent hover:text-accent-foreground transition-colors">
          <div className="flex items-center gap-1 flex-1 justify-start w-4/5">
            {!params.data.labels?.length ? (
              <span className="text-xs text-muted-foreground">
                Select Labels
              </span>
            ) : (
              renderLabels()
            )}
          </div>
          <div className="flex items-center gap-1 justify-center w-1/5">
            <ChevronDown className="h-4 ml-1" />
          </div>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[200px]">
        {labels?.data?.map((label) => (
          <DropdownMenuItem
            key={label._id}
            className="flex items-center gap-2 py-1.5"
            disabled={updatingLabelId === label._id}
            onSelect={(e) => {
              e.preventDefault();
              setUpdatingLabelId(label._id);
              const hasLabel = params.data.labels?.some(
                (l: any) => l._id === label._id
              );
              if (hasLabel) {
                removeLabelsFromDns(
                  {
                    domain: [params.data.domain],
                    labelIds: [label._id],
                    pageMetadata,
                  },
                  {
                    onSettled: () => setUpdatingLabelId(null),
                  }
                );
              } else {
                addLabelsToDns(
                  {
                    domain: [params.data.domain],
                    labelIds: [label._id],
                    pageMetadata,
                  },
                  {
                    onSettled: () => setUpdatingLabelId(null),
                  }
                );
              }
            }}
          >
            <div className="flex items-center gap-2 flex-1">
              <TagIcon weight="fill" size={16} style={{ fill: label.color }} />

              <span className="text-sm">{label.name}</span>
            </div>
            {updatingLabelId === label._id ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              params.data.labels?.some?.((l: any) => l._id === label._id) && (
                <Check className="h-3.5 w-3.5" />
              )
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const createDnsColumnDefs = ({
  pageMetadata,
}: {
  pageMetadata: {
    page: number;
    limit: number;
    search: string;
    selectedLabels: string[];
    selectedEspTypes?: string[];
  };
}): ColDef[] =>
  [
    {
      field: "domain",
      headerName: "Domain",
      sortable: true,
      filter: true,
      minWidth: 200,
    },
    {
      field: "labels",
      headerName: "Labels",
      sortable: false,
      filter: false,
      minWidth: 250,
      flex: 1,
      cellRenderer: (params: any) => (
        <LabelCellRenderer params={params} pageMetadata={pageMetadata} />
      ),
    },
    {
      field: "lastChecked",
      colId: "lastChecked",
      headerName: "Last Checked",
      sortable: true,
      initialSort: "desc" as SortDirection,
      filter: true,
      width: 180,
      minWidth: 200,
      cellRenderer: (params: any) => {
        const lastChecked = params.value;
        return (
          <span className="text-gray-600 dark:text-foreground">
            {dateFormatter(lastChecked)}
          </span>
        );
      },
    },
    {
      colId: "espType",
    field: "espType",
    headerName: "ESP Type",
      minWidth: 120,
      cellRenderer: (params: { value: AuthProvider }) => {
        return (
          <div className="flex items-center h-full">
            <Tooltip>
              <TooltipTrigger asChild>
                {authProviderIcons[params.value || "smtp"]?.icon}
              </TooltipTrigger>
              <TooltipContent>
                {authProviderIcons[params.value]?.tooltip || "Private SMTP"}
              </TooltipContent>
            </Tooltip>
          </div>
        );
      },
    },
    {
      field: "overallScore",
      colId: "overallScore",
      headerName: "Score",
      sortable: true,

      filter: true,
      width: 80,
      minWidth: 80,
      maxWidth: 80,
      cellRenderer: (params: any) => {
        const score = params.value;
        return (
          <div className="flex items-center gap-2 h-full">
            <ScoreBadge score={score} thresholds={{ green: 90, yellow: 70 }} />
          </div>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      sortable: true,
      filter: true,
      width: 130,
      cellRenderer: (params: any) => {
        const status = params.value;
        const colorMap: Record<string, string> = {
          Healthy: "text-green-600 dark:text-green-500",
          Average: "text-yellow-600 dark:text-yellow-500",
          Poor: "text-red-600 dark:text-red-500",
        };
        return <span className={colorMap[status] || ""}>{status}</span>;
      },
    },

    {
      field: "SPF",
      headerName: "SPF",
      sortable: true,
      filter: true,
      width: 120,
    },
    {
      field: "DKIM",
      headerName: "DKIM",
      sortable: true,
      filter: true,
      width: 120,
    },
    {
      field: "DMARC",
      headerName: "DMARC",
      sortable: true,
      filter: true,
      width: 120,
    },
    {
      field: "ARC",
      headerName: "ARC",
      sortable: true,
      filter: true,
      width: 120,
    },
    {
      field: "BIMI",
      headerName: "BIMI",
      sortable: true,
      filter: true,
      width: 120,
    },
    {
      field: "A",
      headerName: "A Record",
      sortable: true,
      filter: true,
      width: 120,
    },
    {
      field: "MX",
      headerName: "MX Record",
      sortable: true,
      filter: true,
      width: 120,
    },
    {
      field: "TXT",
      headerName: "TXT",
      sortable: true,
      filter: true,
      width: 120,
    },
    {
      field: "DNSKEY",
      headerName: "DNSKEY",
      sortable: true,
      filter: true,
      width: 120,
    },
    {
      field: "DNSSEC",
      headerName: "DNSSEC",
      sortable: true,
      filter: true,
      width: 120,
    },
    {
      field: "RPKI",
      headerName: "RPKI",
      sortable: true,
      filter: true,
      width: 120,
    },
    {
      field: "DANE",
      headerName: "DANE",
      sortable: true,
      filter: true,
      width: 120,
    },
    {
      field: "TLS",
      headerName: "TLS",
      sortable: true,
      filter: true,
      width: 120,
    },
    {
      field: "SSL",
      headerName: "SSL",
      sortable: true,
      filter: true,
      width: 120,
    },
  ].map((col) => ({
    ...col,
    cellRenderer:
      col.cellRenderer ||
      ((params: any) => {
        const value = params.value;
        if (value === "valid") {
          return (
            <span className="text-green-600 dark:text-green-500">Valid</span>
          );
        }
        if (value === "invalid") {
          return (
            <span className="text-red-600 dark:text-red-500">Invalid</span>
          );
        }
        return value;
      }),
  }));
